'use client';

import { Input } from '@app/app/_components/Input/Input';
import { AddressInput } from '@app/app/_components/AddressInput/AddressInput';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Heading } from '@app/app/_components/Heading/Heading';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { useTranslations } from 'next-intl';
import { FormState } from '@app/lib/form/contracts/FormState';
import { CountryCode } from '@app/domain/model/CountryCode';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/personal-accidents/_sections/PersonalAccidentsWizardSection/hooks/usePolicyholderFormFields';
import { PurchasePersonalInsuranceResult } from '@app/service/eshop/personal-accidents/actions/purchasePersonalAccidentsInsurance';
import { preventPasting } from '@app/lib/event-handlers/preventPasting';
import { preventDropping } from '@app/lib/event-handlers/preventDropping';
import { CommonType } from '@app/domain/model/CommonType';
import { useIsChromiumBrowser } from '@app/lib/browser/useIsChromiumBrowser';

interface Props {
    formState: FormState<PurchasePersonalInsuranceResult>;
    personalCode: string;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    disabledCheckbox?: boolean;
}

export function PolicyholderStep({
    formState,
    personalCode,
    policyholderFields,
    policyholderHandlers,
    disabledCheckbox,
}: Props) {
    const t = useTranslations();
    const isChromium = useIsChromiumBrowser();

    return (
        <>
            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.travel.personalInformation')}</Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                        name="policyholderFirstName"
                        label={t('eshop.travel.firstNameLabel')}
                        placeholder={t('eshop.travel.firstNamePlaceholder')}
                        value={policyholderFields.firstName}
                        onChange={policyholderHandlers.handleFirstNameChange}
                        error={formState.violations?.policyholderFirstName}
                    />
                    <Input
                        name="policyholderLastName"
                        label={t('eshop.travel.lastNameLabel')}
                        placeholder={t('eshop.travel.lastNamePlaceholder')}
                        value={policyholderFields.lastName}
                        onChange={policyholderHandlers.handleLastNameChange}
                        error={formState.violations?.policyholderLastName}
                    />
                    <input type="hidden" name="policyholderPersonalCode" value={personalCode} />
                    <ReadOnlyField label={t('eshop.travel.personalCodeLabel')}>{personalCode}</ReadOnlyField>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.travel.contactInformation')}</Heading>
                <AddressInput
                    disableCountrySelect
                    defaultCountry={CountryCode.Lithuania}
                    countryOptions={[]}
                    namePrefix="policyholder"
                    violations={formState.violations}
                    defaultValue={policyholderFields.address ?? undefined}
                    onChange={policyholderHandlers.handleAddressChange}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                    <input
                        type="hidden"
                        name="policyholderPhoneNumber"
                        value={`+370${policyholderFields.phoneNumber.replace(/\D/g, '')}`}
                    />
                    <Input
                        label={t('eshop.transport.phoneNumberLabel')}
                        placeholder={t('eshop.transport.phoneNumberPlaceholder')}
                        value={policyholderFields.phoneNumber}
                        onChange={policyholderHandlers.handlePhoneNumberChange}
                        prefix="+370"
                        error={formState.violations?.policyholderPhoneNumber}
                    />
                </div>
                <div>
                    <Input
                        className="md:col-start-1"
                        name="policyholderEmail"
                        label={t('eshop.transport.emailLabel')}
                        placeholder={t('eshop.transport.emailPlaceholder')}
                        description={t('eshop.transport.emailDescription')}
                        value={policyholderFields.email}
                        onChange={policyholderHandlers.handleEmailChange}
                        onPaste={preventPasting}
                        onDrop={preventDropping}
                        autoComplete={isChromium ? 'chrome-off' : 'off'}
                        error={formState.violations?.policyholderEmail}
                    />
                </div>
                <div>
                    <Input
                        name="policyholderEmailConfirmation"
                        label={t('eshop.transport.confirmEmailLabel')}
                        placeholder={t('eshop.transport.confirmEmailPlaceholder')}
                        description={t('eshop.transport.confirmEmailDescription')}
                        value={policyholderFields.emailConfirmation}
                        onChange={policyholderHandlers.handleEmailConfirmationChange}
                        onPaste={preventPasting}
                        onDrop={preventDropping}
                        autoComplete={isChromium ? 'chrome-off' : 'off'}
                        error={formState.violations?.policyholderEmailConfirmation}
                    />
                </div>
            </div>

            {!disabledCheckbox && (
                <CheckboxInput
                    name="policyholderIsInsuredPerson"
                    label={t('eshop.personalAccidents.policyholderIsInsured')}
                    checked={policyholderFields.isInsuredPerson}
                    onChange={policyholderHandlers.handleIsInsuredPersonChange}
                />
            )}

            {disabledCheckbox && (
                <input
                    type="hidden"
                    name="policyholderIsInsuredPerson"
                    value={policyholderFields.isInsuredPerson ? CommonType.True : CommonType.False}
                />
            )}
        </>
    );
}
